import { z } from 'zod';
import { DELIVERY_CHANNELS, DELIVERY_CHANNEL_INFO, type DeliveryChannel } from './delivery.ts';
import { TransactionalSettings } from './transactional.ts';

/**
 * Delivery controls (BB-102, CMP-005…CMP-008): the rules between variant assignment and sending — re-eligibility,
 * rate limits, frequency caps and quiet hours — and the exemptions a message may carry. A campaign keeps its controls
 * and exemptions in the revision (`deliveryControls`, `exemptions`); frequency caps and environment rate limits are
 * settings of the environment.
 */

/** 720 days, the longest re-eligibility window. */
export const REELIGIBILITY_MAX_SECONDS = 62_208_000;
const WallTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:mm');

/**
 * Whether a profile that got the campaign may get it again. `enabled: false` — once per profile; `windowSeconds: 0` —
 * always; otherwise after the window since it was received (a card: since its first impression). No setting at all
 * means always, so campaigns published before BB-102 keep sending as they did.
 */
export const Reeligibility = z.strictObject({ enabled: z.boolean(), windowSeconds: z.number().int().min(0).max(REELIGIBILITY_MAX_SECONDS).default(0) });
export type Reeligibility = z.infer<typeof Reeligibility>;

/** Hours in the profile's time zone when the campaign does not send; `from` after `to` spans midnight (22:00–06:00). */
export const QuietHours = z
  .strictObject({ enabled: z.boolean(), from: WallTime, to: WallTime, fallback: z.enum(['abort', 'next_available']).default('next_available') })
  .refine((q) => q.from !== q.to, { message: 'quiet hours need a start and an end that differ', path: ['to'] });
export type QuietHours = z.infer<typeof QuietHours>;

export const RATE_DELAY_MAX_HOURS = 72;

export const DeliveryControls = z.strictObject({
  reeligibility: Reeligibility.optional(),
  quietHours: QuietHours.optional(),
  /** Sends per minute of this campaign; the environment's channel limit applies too, the lower wins. */
  ratePerMinute: z.number().int().min(1).max(1_000_000).optional(),
  /** A send held back by rate limits longer than this is aborted (`rate_limit_expired`); 72 when not set. */
  rateMaxDelayHours: z.number().int().min(1).max(RATE_DELAY_MAX_HOURS).optional(),
  /** Settings of a transactional campaign (BB-78): staleness, dedupe, subscription group. */
  transactional: TransactionalSettings.optional(),
});
export type DeliveryControls = z.infer<typeof DeliveryControls>;

/**
 * What a message may skip. Changing any bypass needs `messages.exemptions.manage`; a transactional campaign (trading
 * push) skips everything and does not count towards caps, whatever is stored.
 */
export const MessageExemptions = z.strictObject({
  bypassSuppression: z.boolean().optional(),
  bypassGlobalControl: z.boolean().optional(),
  bypassFrequencyCap: z.boolean().optional(),
  bypassQuietHours: z.boolean().optional(),
  bypassRateLimit: z.boolean().optional(),
  /** Whether a send counts towards frequency caps (default true). */
  countTowardsCaps: z.boolean().optional(),
});
export type MessageExemptions = z.infer<typeof MessageExemptions>;

export const BYPASS_FLAGS = ['bypassSuppression', 'bypassGlobalControl', 'bypassFrequencyCap', 'bypassQuietHours', 'bypassRateLimit'] as const;

export interface EffectiveExemptions {
  bypassSuppression: boolean;
  bypassGlobalControl: boolean;
  bypassFrequencyCap: boolean;
  bypassQuietHours: boolean;
  bypassRateLimit: boolean;
  countTowardsCaps: boolean;
}

export function effectiveExemptions(category: 'marketing' | 'transactional', stored: MessageExemptions | Record<string, unknown> = {}): EffectiveExemptions {
  if (category === 'transactional') return { bypassSuppression: true, bypassGlobalControl: true, bypassFrequencyCap: true, bypassQuietHours: true, bypassRateLimit: true, countTowardsCaps: false };
  const flag = (key: string) => (stored as Record<string, unknown>)[key] === true;
  return {
    bypassSuppression: flag('bypassSuppression'),
    bypassGlobalControl: flag('bypassGlobalControl'),
    bypassFrequencyCap: flag('bypassFrequencyCap'),
    bypassQuietHours: flag('bypassQuietHours'),
    bypassRateLimit: flag('bypassRateLimit'),
    countTowardsCaps: (stored as Record<string, unknown>).countTowardsCaps !== false,
  };
}

/** Channels that count towards frequency caps: in-app messages and content cards never do. */
export const CAP_CHANNELS = ['push', 'webhook'] as const;
export type CapChannel = (typeof CAP_CHANNELS)[number];
export const capChannelOf = (channel: DeliveryChannel): CapChannel | null => {
  const family = DELIVERY_CHANNEL_INFO[channel].channel;
  return (CAP_CHANNELS as readonly string[]).includes(family) ? (family as CapChannel) : null;
};

export const FREQUENCY_CAP_MAX_ACTIVE = 10;
export const FREQUENCY_CAP_MAX_WINDOW_DAYS = 30;
const WINDOW_MINUTES = { minutes: 1, days: 1_440, weeks: 10_080 } as const;
export const CapWindow = z
  .strictObject({ value: z.number().int().min(1), unit: z.enum(['minutes', 'days', 'weeks']) })
  .refine((w) => w.value * WINDOW_MINUTES[w.unit] <= FREQUENCY_CAP_MAX_WINDOW_DAYS * 1_440, { message: `a window is at most ${FREQUENCY_CAP_MAX_WINDOW_DAYS} days` });
export type CapWindow = z.infer<typeof CapWindow>;

/**
 * «At most `maxCount` messages of these channels (of campaigns with this tag) per window». Days and weeks are calendar
 * days in the profile's time zone; minutes slide. Rules combine with AND: the first one reached caps the send.
 */
export const FrequencyCapRuleBody = z.strictObject({
  name: z.string().trim().min(1).max(120),
  channels: z.array(z.enum(CAP_CHANNELS)).min(1),
  tagId: z.uuid().nullable().default(null),
  maxCount: z.number().int().min(1).max(10_000),
  window: CapWindow,
  enabled: z.boolean().default(true),
});
export type FrequencyCapRuleBody = z.infer<typeof FrequencyCapRuleBody>;
export const UpdateFrequencyCapRuleBody = FrequencyCapRuleBody.partial().refine((b) => Object.keys(b).length > 0, 'nothing to change');
export type UpdateFrequencyCapRuleBody = z.infer<typeof UpdateFrequencyCapRuleBody>;

export interface FrequencyCapRuleView extends FrequencyCapRuleBody {
  id: string;
  createdAt: string;
  updatedAt: string;
}

/** Sends per minute of all campaigns of the environment on one channel family. */
export const RATE_LIMIT_CHANNELS = [...new Set(DELIVERY_CHANNELS.map((c) => DELIVERY_CHANNEL_INFO[c].channel))] as Array<(typeof DELIVERY_CHANNEL_INFO)[DeliveryChannel]['channel']>;
export const PutRateLimitsBody = z.strictObject({
  limits: z
    .array(z.strictObject({ channel: z.enum(RATE_LIMIT_CHANNELS as [string, ...string[]]), perMinute: z.number().int().min(1).max(1_000_000) }))
    .max(RATE_LIMIT_CHANNELS.length)
    .refine((l) => new Set(l.map((x) => x.channel)).size === l.length, 'one limit per channel'),
});
export type PutRateLimitsBody = z.infer<typeof PutRateLimitsBody>;
export interface RateLimitsView {
  limits: Array<{ channel: string; perMinute: number }>;
}

/** Reasons of `suppressed` / `capped` / `deferred` / `aborted` facts written by delivery controls. */
export const POLICY_REASONS = ['not_reeligible', 'frequency_cap', 'rate_limit', 'rate_limit_expired', 'quiet_hours'] as const;
